import { useEffect, useState } from 'react';
import HealthInfo from './HealthInfo';
import HealthChart from './HealthChart';
import ControlPanel from './ControlPanel';
import AboutPage from './AboutPage';
import { AppBar, BottomNavigation, BottomNavigationAction, IconButton, Toolbar, Typography, TextField, Button, Snackbar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MenuIcon from '@material-ui/icons/Menu';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import SettingsRemoteIcon from '@material-ui/icons/SettingsRemote';
import InfoIcon from '@material-ui/icons/Info';
import UpdateIcon from '@material-ui/icons/Update';
import Alert from '@material-ui/lab/Alert';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    transition: 'background-image 2s ease-in-out',
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
  },
  header: {
    position: 'sticky',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1100,
  },
  main: {
    flexGrow: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  footer: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    position: 'sticky',
    bottom: 0,
    left: 0,
    right: 0,
  },
  loginContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
  },
  loginButton: {
    marginTop: theme.spacing(2),
  }
}));

// State to manage background image for the whole app
const backgroundImages = [
  'ray.jpeg',
];

function App() {
  const classes = useStyles();
  const [value, setValue] = useState<number>(() => {
    const savedValue = sessionStorage.getItem('selectedTab');
    return savedValue ? parseInt(savedValue) : 0;
  });
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return sessionStorage.getItem('authenticated') === 'true';
  });
  const [password, setPassword] = useState<string>('');
  // State to handle login error notification
  const [loginError, setLoginError] = useState(false);

  // Save the current tab to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('selectedTab', value.toString());
  }, [value]);

  // Genarate passwd hash
  const hashPassword = async (password: string) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  // Handle password authentication
  const handleLogin = async () => {
    const correctPasswordHash = '26ea47ddf8d0ca6329f2fe3d84e037a9bdc7d1578f0f37e40c0fef18f660fa16'; // hash of reilkay
    if (await hashPassword(password) === correctPasswordHash) {
      setIsAuthenticated(true);
      sessionStorage.setItem('authenticated', 'true'); // Mark as authenticated
    } else {
      setLoginError(true);
    }
  };

  // Close Snackbar
  const handleLoginErrorClose = () => {
    setLoginError(false);  // Hide the error Snackbar when closed
  };

  // Render content based on the current value of the BottomNavigation
  const renderContent = () => {
    switch (value) {
      case 0:
        return <HealthInfo />;
      case 1:
        return <HealthChart />;
      case 2:
        return <ControlPanel />;
      case 3:
        return <AboutPage onBackgroundChange={handleBackgroundChange} />;
      default:
        return <HealthInfo />; // Default page  
    }
  };

  // listen for the Enter key.
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleLogin(); // 当用户按下 Enter 键时，触发登录
    }
  };

  const [backgroundIndex, setBackgroundIndex] = useState<number | null>(() => {
    const savedIndex = localStorage.getItem('backgroundIndex');
    return savedIndex ? parseInt(savedIndex, 10) : null; // null means no background
  });

  const handleBackgroundChange = () => {
    const newIndex = (backgroundIndex !== null ? backgroundIndex + 1 : 0) % (backgroundImages.length + 1);
    setBackgroundIndex(newIndex === backgroundImages.length ? null : newIndex);
    localStorage.setItem('backgroundIndex', newIndex === backgroundImages.length ? '' : newIndex.toString());
  };

  // Apply the background image directly to the body (so it doesn't scroll)
  useEffect(() => {
    if (backgroundIndex !== null) {
      document.body.style.backgroundImage = `url(${backgroundImages[backgroundIndex]})`;
    } else {
      document.body.style.backgroundImage = 'none'; // No background by default
    }
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center';
    document.body.style.backgroundAttachment = 'fixed'; // Fix the background in place
  }, [backgroundIndex]);

  // If not authenticated, render the password input screen
  if (!isAuthenticated) {
    return (
      <div className={classes.loginContainer}>
        <Typography variant="h6">
          Health Information Dashboard
        </Typography>
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyUp={handleKeyPress}
        />
        <Button
          className={classes.loginButton}
          variant="contained"
          color="primary"
          onClick={handleLogin}
        >
          Login
        </Button>
        {/* Snackbar for error message */}
        <Snackbar
          open={loginError}
          autoHideDuration={6000}
          onClose={handleLoginErrorClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleLoginErrorClose} severity="error">
            Incorrect password, please try again.
          </Alert>
        </Snackbar>
      </div>
    );
  }

  return (
    <div
      className={classes.root}
    >
      <header className={classes.header}>
        <AppBar position="static">
          <Toolbar>
            <IconButton edge="start" className={classes.menuButton} color="inherit" aria-label="menu">
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" className={classes.title}>
              Health Information Dashboard
            </Typography>
          </Toolbar>
        </AppBar>
      </header>

      {/* Main content */}
      <main className={classes.main}>
        {renderContent()}
      </main>

      {/* Footer */}
      <footer className={classes.footer}>
        <BottomNavigation
          value={value}
          onChange={(event, newValue) => {
            setValue(newValue); // Update the navigation value to switch pages
          }}
          showLabels
        >
          <BottomNavigationAction label="Data" icon={<UpdateIcon />} />
          <BottomNavigationAction label="Chart" icon={<EqualizerIcon />} />
          <BottomNavigationAction label="Control" icon={<SettingsRemoteIcon />} />
          <BottomNavigationAction label="About" icon={<InfoIcon />} />
        </BottomNavigation>
      </footer>
    </div>
  );
}

export default App;
